import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import {
  Settings,
  Cpu,
  Sparkles,
  Brain,
  Shuffle,
  Check,
} from 'lucide-react';
import { TAO6PretrainedModel } from '@/mock/gpu-instance';

export interface TAO6TrainingConfig {
  epochs: number;
  batchSize: number;
  learningRate: number;
  optimizer: 'adam' | 'sgd' | 'adamw';
  augmentations: string[];
  enableQAT: boolean;
  enableKD: boolean;
  multiGPU: boolean;
  gpuCount: number;
}

interface TAO6ConfigBuilderProps {
  config: TAO6TrainingConfig;
  onChange: (config: TAO6TrainingConfig) => void;
  selectedModel?: TAO6PretrainedModel;
}

const AUGMENTATION_OPTIONS = [
  { id: 'random_flip', label: 'Random Flip', description: 'Horizontal/vertical flip' },
  { id: 'random_crop', label: 'Random Crop', description: 'Random image cropping' },
  { id: 'color_jitter', label: 'Color Jitter', description: 'Random color adjustments' },
  { id: 'mosaic', label: 'Mosaic', description: 'Combine 4 images (detection)' },
  { id: 'mixup', label: 'MixUp', description: 'Blend two images' },
  { id: 'cutmix', label: 'CutMix', description: 'Cut and paste patches' },
  { id: 'cutout', label: 'Cutout', description: 'Random rectangular masking' },
  { id: 'autoaugment', label: 'AutoAugment', description: 'Learned augmentation policies' },
  { id: 'hsv_augment', label: 'HSV Augment', description: 'Hue/saturation/value shifts' },
];

const TAO6ConfigBuilder = ({
  config,
  onChange,
  selectedModel,
}: TAO6ConfigBuilderProps) => {
  const handleChange = <K extends keyof TAO6TrainingConfig>(
    key: K,
    value: TAO6TrainingConfig[K]
  ) => {
    onChange({ ...config, [key]: value });
  };

  const toggleAugmentation = (augId: string) => {
    const newAugs = config.augmentations.includes(augId)
      ? config.augmentations.filter((a) => a !== augId)
      : [...config.augmentations, augId];
    handleChange('augmentations', newAugs);
  };

  const availableAugmentations = selectedModel
    ? AUGMENTATION_OPTIONS.filter((aug) =>
        selectedModel.supportedAugmentations.includes(aug.id)
      )
    : AUGMENTATION_OPTIONS;

  return (
    <div className="space-y-6">
      {/* Basic Training Parameters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Training Parameters
          </CardTitle>
          <CardDescription>Configure the basic training hyperparameters</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Epochs */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Epochs</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.epochs}</span>
            </div>
            <Slider
              value={[config.epochs]}
              onValueChange={([value]) => handleChange('epochs', value)}
              min={10}
              max={200}
              step={5}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>10</span>
              <span>200</span>
            </div>
          </div>

          {/* Batch Size */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Batch Size</Label>
              <span className="text-sm text-muted-foreground font-mono">{config.batchSize}</span>
            </div>
            <Slider
              value={[config.batchSize]}
              onValueChange={([value]) => handleChange('batchSize', value)}
              min={1}
              max={64}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1</span>
              <span>64</span>
            </div>
          </div>

          {/* Learning Rate */}
          <div className="space-y-2">
            <Label>Learning Rate</Label>
            <Select
              value={config.learningRate.toString()}
              onValueChange={(value) => handleChange('learningRate', parseFloat(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.01">0.01 (High)</SelectItem>
                <SelectItem value="0.001">0.001 (Default)</SelectItem>
                <SelectItem value="0.0001">0.0001 (Low)</SelectItem>
                <SelectItem value="0.00001">0.00001 (Very Low)</SelectItem>
                <SelectItem value="0.00005">0.00005 (Fine-tuning)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Optimizer */}
          <div className="space-y-2">
            <Label>Optimizer</Label>
            <Select
              value={config.optimizer}
              onValueChange={(value) => handleChange('optimizer', value as TAO6TrainingConfig['optimizer'])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="adamw">AdamW (Recommended)</SelectItem>
                <SelectItem value="adam">Adam</SelectItem>
                <SelectItem value="sgd">SGD with Momentum</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Data Augmentation */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shuffle className="h-4 w-4" />
            Data Augmentation
          </CardTitle>
          <CardDescription>
            Select augmentation techniques
            {selectedModel && (
              <span className="ml-1">
                (compatible with {selectedModel.displayName})
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {availableAugmentations.map((aug) => {
              const isSelected = config.augmentations.includes(aug.id);
              return (
                <div
                  key={aug.id}
                  className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-muted-foreground/50'
                  }`}
                  onClick={() => toggleAugmentation(aug.id)}
                >
                  <div
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border mt-0.5 ${
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground/50'
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{aug.label}</div>
                    <div className="text-xs text-muted-foreground">{aug.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {config.augmentations.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="text-xs text-muted-foreground mb-2">Selected ({config.augmentations.length}):</div>
              <div className="flex flex-wrap gap-1">
                {config.augmentations.map((aug) => (
                  <Badge key={aug} variant="secondary" className="text-xs">
                    {AUGMENTATION_OPTIONS.find((a) => a.id === aug)?.label || aug}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Advanced Features
          </CardTitle>
          <CardDescription>Enable TAO 6.x advanced training capabilities</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quantization-Aware Training */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-4 w-4 text-yellow-500" />
              <div>
                <div className="text-sm font-medium">Quantization-Aware Training (QAT)</div>
                <div className="text-xs text-muted-foreground">
                  Train model to be INT8 deployment-ready
                </div>
              </div>
            </div>
            <Switch
              checked={config.enableQAT}
              onCheckedChange={(checked) => handleChange('enableQAT', checked)}
              disabled={selectedModel && !selectedModel.supportsQAT}
            />
          </div>
          {selectedModel && !selectedModel.supportsQAT && (
            <div className="text-xs text-muted-foreground pl-7">
              QAT not supported by {selectedModel.displayName}
            </div>
          )}

          {/* Knowledge Distillation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="h-4 w-4 text-purple-500" />
              <div>
                <div className="text-sm font-medium">Knowledge Distillation</div>
                <div className="text-xs text-muted-foreground">
                  Transfer knowledge from a larger teacher model
                </div>
              </div>
            </div>
            <Switch
              checked={config.enableKD}
              onCheckedChange={(checked) => handleChange('enableKD', checked)}
              disabled={selectedModel && !selectedModel.supportsKD}
            />
          </div>

          {/* Multi-GPU */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cpu className="h-4 w-4 text-green-500" />
              <div>
                <div className="text-sm font-medium">Multi-GPU Training</div>
                <div className="text-xs text-muted-foreground">
                  Distribute training across multiple GPUs
                </div>
              </div>
            </div>
            <Switch
              checked={config.multiGPU}
              onCheckedChange={(checked) => handleChange('multiGPU', checked)}
            />
          </div>

          {config.multiGPU && (
            <div className="pl-7 space-y-2">
              <Label className="text-sm">GPU Count</Label>
              <Select
                value={config.gpuCount.toString()}
                onValueChange={(value) => handleChange('gpuCount', parseInt(value))}
              >
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 GPUs</SelectItem>
                  <SelectItem value="4">4 GPUs</SelectItem>
                  <SelectItem value="8">8 GPUs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Summary */}
      <Card className="bg-muted/30">
        <CardContent className="pt-4">
          <div className="text-sm font-medium mb-2">Configuration Summary</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Epochs:</div>
            <div className="font-mono">{config.epochs}</div>
            <div className="text-muted-foreground">Batch Size:</div>
            <div className="font-mono">{config.batchSize}</div>
            <div className="text-muted-foreground">Learning Rate:</div>
            <div className="font-mono">{config.learningRate}</div>
            <div className="text-muted-foreground">Optimizer:</div>
            <div className="font-mono capitalize">{config.optimizer}</div>
            <div className="text-muted-foreground">Augmentations:</div>
            <div className="font-mono">{config.augmentations.length}</div>
            <div className="text-muted-foreground">QAT:</div>
            <div className="font-mono">{config.enableQAT ? 'Enabled' : 'Disabled'}</div>
            <div className="text-muted-foreground">Multi-GPU:</div>
            <div className="font-mono">
              {config.multiGPU ? `${config.gpuCount} GPUs` : 'Disabled'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TAO6ConfigBuilder;
